/**
 * Chat Header Component
 * Header for chat window with navigation and action buttons
 *
 * Accessibility:
 * - 44px minimum touch targets on mobile
 * - Keyboard shortcut hints in tooltips
 * - Proper ARIA labeling and roles
 * - Respects reduced motion preferences
 */

import React from 'react';
import { motion } from 'motion/react';
import { History, MessageSquare, X, Plus } from 'lucide-react';
import { cn } from '../../../../lib/utils';
import { Tooltip } from '../../../../shared/ui/tooltip/Tooltip';
import { useReducedMotion } from '../../../../shared/hooks/useReducedMotion';
import { OfflineBadge } from './OfflineIndicator';

interface ChatHeaderProps {
  onClose: () => void;
  onToggleHistory: () => void;
  onNewChat?: () => void;
  chatbotName?: string;
  isAuthenticated?: boolean;
  className?: string;
}

/**
 * Chat window header with navigation and controls
 * Emerald gradient background matching brand colors
 */
export const ChatHeader: React.FC<ChatHeaderProps> = ({
  onClose,
  onToggleHistory,
  onNewChat,
  chatbotName = 'Ask Hajra',
  isAuthenticated = false,
  className
}) => {
  const { prefersReducedMotion, tween } = useReducedMotion();

  // Shared button styles for consistency
  const buttonClasses = cn(
    // Base size with mobile minimum touch target
    'w-10 h-10 min-w-[44px] min-h-[44px]',
    'rounded-full',

    // Colors
    'bg-white/15 hover:bg-white/25 active:bg-white/30',
    'text-white',

    // Layout
    'flex items-center justify-center',

    // Transitions (color only, no scale to avoid layout shifts)
    'transition-colors duration-150',

    // Focus states
    'focus:outline-hidden focus:ring-2 focus:ring-white/50 focus:ring-offset-0',
    'focus-visible:ring-2 focus-visible:ring-white/50'
  );

  return (
    <motion.header
      initial={prefersReducedMotion ? { opacity: 0 } : { y: -20, opacity: 0 }}
      animate={prefersReducedMotion ? { opacity: 1 } : { y: 0, opacity: 1 }}
      transition={tween}
      className={cn(
        // Height with mobile adjustment
        'h-16 min-h-[64px] px-4',
        'max-md:min-h-[56px] max-md:h-14',

        // Background
        'bg-linear-to-r from-emerald-600 to-emerald-700',

        // Layout
        'flex items-center justify-between gap-2',

        // Elevation
        'shadow-xs relative z-10',

        className
      )}
      role="banner"
      aria-label="Chat header"
    >
      {/* Left Side Actions */}
      <div className="flex items-center gap-1.5" role="group" aria-label="Navigation">
        {/* New Chat Button */}
        {onNewChat && (
          <Tooltip content="New chat" side="bottom">
            <button
              onClick={onNewChat}
              className={buttonClasses}
              aria-label="Start new conversation"
              type="button"
            >
              <Plus className="w-5 h-5" aria-hidden="true" />
            </button>
          </Tooltip>
        )}
      </div>

      {/* Title */}
      <div className="flex items-center gap-2 flex-1 justify-center min-w-0">
        <MessageSquare className="w-5 h-5 text-white/90 shrink-0" aria-hidden="true" />
        <h1 className="text-lg font-display font-semibold text-white truncate">
          {chatbotName}
        </h1>
        <OfflineBadge className="ml-1" />
      </div>

      {/* Right Side Actions */}
      <div className="flex items-center gap-1.5" role="group" aria-label="Actions">
        {/* History Button - Only show if authenticated */}
        {isAuthenticated && (
          <Tooltip content="Chat history" side="bottom">
            <button
              onClick={onToggleHistory}
              className={buttonClasses}
              aria-label="View saved conversations"
              type="button"
            >
              <History className="w-5 h-5" aria-hidden="true" />
            </button>
          </Tooltip>
        )}

        {/* Close Button */}
        <Tooltip content="Close (Esc)" side="bottom">
          <button
            onClick={onClose}
            className={buttonClasses}
            aria-label="Close chat"
            type="button"
          >
            <X className="w-5 h-5" aria-hidden="true" />
          </button>
        </Tooltip>
      </div>
    </motion.header>
  );
};

export default ChatHeader;
